"use client";
import type { ReactElement, RefObject } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { AnimatePresence, useInView, useScroll, useTransform, } from "framer-motion";
import type { WitnessCardData } from "@/components/ui/Cwitnesscard";
import { Cwitnesscardmodal } from "@/components/ui/Cwitnesscardmodal";
import { useViewport, useViewportMetrics } from "@/hooks/dom";
import { useScrollContainer } from "@/contexts/layout";
import { Viewport } from "@/theme";
import { CwitnessLeftColumn } from "./CwitnessLeftColumn";
import { CwitnessRightColumn } from "./CwitnessRightColumn";
import { CWITNESS_IN_VIEW_AMOUNT } from "./constants";
import { getParallaxTextSize, resolveHydratedRightColumnRef, resolveParallaxTextSpacing, shouldRenderWitnessContent, } from "./selectors";
import { buildRelativeContainerStyle } from "./styles";
import { useMobileScrollChain } from "./useMobileScrollChain";
import { useParallaxTextScrollRange } from "./useParallaxTextScrollRange";
import type { CwitnessContentProps } from "./types";
export function CwitnessContent({ sectionRef, textTopSpacing, textBottomSpacing, }: CwitnessContentProps): ReactElement {
    const { viewport } = useViewport();
    const { vh } = useViewportMetrics();
    const isMobile = viewport === Viewport.Mobile;
    const isCompact = viewport === Viewport.Compact;
    const parallaxTextSize = getParallaxTextSize({ isMobile });
    const leftColumnRef = useRef<HTMLDivElement>(null);
    const parallaxScrollRef = useRef<HTMLDivElement>(null);
    const parallaxContentRef = useRef<HTMLDivElement>(null);
    const { containerRef: outerScrollRef } = useScrollContainer();
    const isSectionInView = useInView(sectionRef, {
        root: outerScrollRef ?? undefined,
        amount: CWITNESS_IN_VIEW_AMOUNT,
    });
    const [selectedCard, setSelectedCard] = useState<WitnessCardData | null>(null);
    const [rightColumnElement, setRightColumnElement] = useState<HTMLDivElement | null>(null);
    const shouldRenderContent = shouldRenderWitnessContent({
        isSectionInView,
        selectedCard,
    });
    const hydratedRightColumnRef = useMemo<RefObject<HTMLDivElement | null> | undefined>(() => {
        return resolveHydratedRightColumnRef({
            rightColumnElement,
            shouldRenderContent,
        });
    }, [rightColumnElement, shouldRenderContent]);
    const setRightColumnRef = useCallback((element: HTMLDivElement | null): void => {
        setRightColumnElement(element);
    }, []);
    const parallaxTextScrollRange = useParallaxTextScrollRange({
        contentRef: parallaxContentRef,
        isMobile,
        parallaxTextSize,
        scrollRef: parallaxScrollRef,
        shouldRenderContent,
        textBottomSpacing,
        textTopSpacing,
        vh,
    });
    useMobileScrollChain({
        innerElement: rightColumnElement,
        isMobile,
        outerScrollRef,
        shouldRenderContent,
    });
    const { scrollYProgress } = useScroll({
        container: hydratedRightColumnRef,
    });
    const parallaxTextY = useTransform(scrollYProgress, [0, 1], [0, -parallaxTextScrollRange]);
    const parallaxTextSpacing = resolveParallaxTextSpacing({
        isMobile,
        textBottomSpacing,
        textTopSpacing,
    });
    const relativeContainerStyle = buildRelativeContainerStyle({ isMobile, vh });
    const handleCardClick = useCallback((card: WitnessCardData): void => {
        setSelectedCard(card);
    }, []);
    const handleCloseModal = useCallback((): void => {
        setSelectedCard(null);
    }, []);
    return (
      <>
        <div style={relativeContainerStyle}>
          {shouldRenderContent && (<>
              <CwitnessLeftColumn isCompact={isCompact} isMobile={isMobile} leftColumnRef={leftColumnRef} parallaxContentRef={parallaxContentRef} parallaxScrollRef={parallaxScrollRef} parallaxTextSize={parallaxTextSize} parallaxTextSpacing={parallaxTextSpacing} parallaxTextY={parallaxTextY} vh={vh}/>

              <CwitnessRightColumn isMobile={isMobile} onCardClick={handleCardClick} rightColumnRef={setRightColumnRef} scrollContainerRef={hydratedRightColumnRef} vh={vh}/>
            </>)}
        </div>

        <AnimatePresence>
          {selectedCard && (<Cwitnesscardmodal card={selectedCard} layoutId={selectedCard.id} onClose={handleCloseModal}/>)}
        </AnimatePresence>
      </>
    );
}
